import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Container } from './Container';
import { PawIcon } from './icons';

const TONES = {
  cream: 'bg-cream-100 text-brown-900',
  sand: 'bg-cream-300 text-brown-900',
  white: 'bg-white text-brown-900',
  dark: 'bg-brown-900 text-cream-100 bg-paw-pattern',
} as const;

interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  tone?: keyof typeof TONES;
  size?: 'sm' | 'md' | 'lg';
  container?: 'sm' | 'md' | 'lg' | 'full' | false;
}

const PADDING = {
  sm: 'py-10 lg:py-14',
  md: 'py-14 lg:py-20',
  lg: 'py-16 lg:py-28',
} as const;

/** Vertical rhythm + background tone for a page section. */
export function Section({
  tone = 'cream',
  size = 'md',
  container = 'lg',
  className,
  children,
  ...props
}: SectionProps) {
  return (
    <section className={cn(TONES[tone], PADDING[size], className)} {...props}>
      {container === false ? children : <Container size={container}>{children}</Container>}
    </section>
  );
}

interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: 'center' | 'left';
  /** Inverts colours for use on the dark tone. */
  inverse?: boolean;
  className?: string;
  children?: ReactNode;
}

/** Eyebrow + title + description, styled once for the whole site. */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'center',
  inverse = false,
  className,
  children,
}: SectionHeadingProps) {
  return (
    <header
      className={cn(
        'flex flex-col gap-3',
        align === 'center' ? 'mx-auto max-w-2xl items-center text-center' : 'items-start text-left',
        className,
      )}
    >
      {eyebrow && (
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-display text-xs font-bold tracking-wide uppercase',
            inverse ? 'bg-caramel-500/20 text-caramel-400' : 'bg-orange-100 text-orange-700',
          )}
        >
          <PawIcon className="size-3.5" />
          {eyebrow}
        </span>
      )}

      <h2
        className={cn(
          'text-3xl leading-[1.12] font-extrabold text-balance lg:text-[2.75rem]',
          inverse ? 'text-cream-100' : 'text-brown-900',
        )}
      >
        {title}
      </h2>

      {description && (
        <p
          className={cn('text-base leading-relaxed', inverse ? 'text-cream-300' : 'text-brown-700')}
        >
          {description}
        </p>
      )}

      {children}
    </header>
  );
}
